import { useState } from "react";
import { XMarkIcon, PlusIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import type { UseWatchlistCreatorReturn } from "../hooks/useWatchlistCreator";
import { resolveTerms } from "@/shared/utils/resolveTerms";
import { containsKeywordDelimiter, parsePastedKeywords } from "../parsePastedList";

interface TermsInputProps {
  creator: UseWatchlistCreatorReturn;
  /** Rendered on the label row, right-aligned — for actions that fill this
   *  same field, like a CSV upload. */
  labelAction?: React.ReactNode;
}

/**
 * One field for everything a watchlist can contain.
 *
 * Whether an entry is a ticker, a keyword or a tag is our storage detail, so
 * the user is no longer asked to choose before they've typed anything. The tag
 * lookup decides: "AAPL", "Apple" and "AAPL.O" all become the same ticker,
 * "OPEC" stays a word but picks up the Energy tag, and anything unrecognised
 * stays a plain keyword — which still matches text on every feed, so nothing
 * is ever lost to a failed lookup.
 */
export default function TermsInput({ creator, labelAction }: TermsInputProps) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const submit = async (raw: string) => {
    const terms = raw
      .split(/[\n,;\t]+/)
      .map((term) => term.trim())
      .filter(Boolean);
    if (terms.length === 0 || busy) return;

    setValue("");
    setBusy(true);
    setProgress(terms.length > 3 ? { done: 0, total: terms.length } : null);
    try {
      const { resolved } = await resolveTerms(terms, {
        onProgress: (done, total) =>
          setProgress(total > 3 ? { done, total } : null),
      });
      resolved.forEach(creator.addResolvedTerm);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const tickerChips = creator.tickers.map((term) => ({
    term,
    kind: "ticker" as const,
    tagId: creator.termTags[term],
  }));
  const keywordChips = creator.keywords.map((term) => ({
    term,
    kind: "keyword" as const,
    tagId: creator.termTags[term],
  }));
  const chips = [...tickerChips, ...keywordChips];

  return (
    <div>
      <div className="flex items-center gap-3">
        <label className="text-xs font-medium uppercase tracking-wide text-gray-400">
          Enter or paste tickers, companies or topics
        </label>
        {labelAction && <span className="ml-auto shrink-0">{labelAction}</span>}
      </div>

      <div className="mt-1 flex gap-1.5">
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit(value);
            }
          }}
          onPaste={(e) => {
            const text = e.clipboardData.getData("text");
            if (!containsKeywordDelimiter(text)) return;
            e.preventDefault();
            submit(parsePastedKeywords(text).join("\n"));
          }}
          placeholder="e.g. AAPL, Apple, OPEC"
          disabled={busy}
          className="flex-1 rounded border border-gray-700 bg-gray-800 px-3 py-1.5 text-sm text-gray-100 placeholder:text-gray-600 focus:border-squawk-blue focus:outline-none disabled:opacity-60"
        />
        <button
          onClick={() => submit(value)}
          disabled={busy || !value.trim()}
          className="cursor-pointer rounded bg-gray-700 px-2 py-1.5 text-xs text-gray-300 transition-colors hover:bg-gray-600 disabled:opacity-40"
        >
          <PlusIcon className="size-3.5" />
        </button>
      </div>

      {/* Only while it is actually doing something. The old line explained the
          field to someone already looking at it, and cost a row of permanent
          small print to do it. */}
      {busy && (
        <p className="mt-1 text-xs text-gray-400">
          {progress ? `Checking ${progress.done} of ${progress.total}…` : "Checking…"}
        </p>
      )}

      {chips.length > 0 && (
        <div className="mt-2 flex max-h-48 flex-wrap gap-1 overflow-y-auto scrollbar-thin">
          {chips.map(({ term, kind, tagId }) => (
            <span
              key={`${kind}-${term}`}
              className={clsx(
                "inline-flex items-center gap-1 rounded border py-0.5 pl-2 pr-1 text-xs",
                kind === "ticker"
                  ? "border-blue-800/30 bg-blue-900/20 text-blue-300"
                  : "border-purple-800/30 bg-purple-900/20 text-purple-300"
              )}
            >
              {term}

              {/* A topic pairing widens what matches — stories that never say
                  the word — so it stays visible and removable. An instrument's
                  tag names the same company and is not shown as a choice. */}
              {tagId && kind === "keyword" && (
                <button
                  onClick={() => creator.dropTermTag(term)}
                  title="Also matching this topic's tag — click to match the word only"
                  className="cursor-pointer rounded-sm border-none bg-squawk-blue/20 px-1 text-[9px] font-semibold text-squawk-blue transition-colors hover:bg-red-500/20 hover:text-red-300"
                >
                  + topic
                </button>
              )}
              {tagId && kind === "ticker" && (
                <span
                  title="Matched to a Newsquawk tag — finds this company even when the ticker isn't written"
                  className="rounded-sm bg-green-500/15 px-1 text-[9px] font-semibold text-green-400"
                >
                  ✓
                </span>
              )}

              <button
                onClick={() =>
                  kind === "ticker" ? creator.removeTicker(term) : creator.removeKeyword(term)
                }
                aria-label={`Remove ${term}`}
                className={clsx(
                  "cursor-pointer border-none bg-transparent p-0.5",
                  kind === "ticker"
                    ? "text-blue-400 hover:text-blue-200"
                    : "text-purple-400 hover:text-purple-200"
                )}
              >
                <XMarkIcon className="size-2.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      {creator.errors.tickers && (
        <p className="mt-1 text-xs text-red-400">{creator.errors.tickers}</p>
      )}
      {creator.errors.keywords && (
        <p className="mt-1 text-xs text-red-400">{creator.errors.keywords}</p>
      )}
    </div>
  );
}
